import { Filter } from "../Models/Filter";
import { FilterCheck } from "./FilterCheck";

// ===============================================================================

/**
 * Abstract class for filtering objects. This is the base class for all filtering
 * managers.
 * 
 * TData is the type of the objects we are filtering
*/
export abstract class FilteringManager<TData>{
    // ===============================================================================

    /**
     * Does the main filtering operation. Given an object to compare and a list of filter checks,
     * this method, iterates through all the checks, keeping track of which checks an object
     * passed. 
     * 
     * If there are 2 filters with the same filter id, they are OR'd together, meaning the object 
     * has to pass at least one of them. Filters with different ids are AND'd together, meaning
     * object has to pass all of them
     * 
     * @param object the object to see if it passes the checks.
     * @param checks filter checks
     */
    protected doesObjectPassChecks(object: TData, checks: FilterCheck<TData>[]) {
        const passedMap =
            checks.reduce(
                (accumulator, currentFilter) => {
                    accumulator[currentFilter.getActiveFilter().filterId] = false;

                    return accumulator;
                },
                {} as { [filterId: string]: boolean }
            );

        for (let check of checks) {
            const passesFilter = check.passesCheck(object);

            if (passesFilter) {
                passedMap[check.getActiveFilter().filterId] = true;
            }
        }

        return Object.keys(passedMap).every(filterId => passedMap[filterId]);
    }
}

// ===============================================================================

/**
 * Abstract class for filtering objects. Subclasses must override the factory method
 * which determines which filter checks to instantiate.
 * 
 * TData is the type of the objects we are filtering
*/
export abstract class FilteringManagerNoContext<TData> extends FilteringManager<TData>{

    // ===============================================================================

    /**
     * This method uses a given filter id to instantiate the correct filter check class,
     * passing the context to this class if needed.
     * 
     * @param filter the active filter to determine what filter check corresponds to it.
     */
    protected abstract filterCheckFactory(filter: Filter): FilterCheck<TData>;

    // ===============================================================================

    /**
     * Given a list of objects, context for those objects, and a list of filters,
     * this method returns the filtered list of those objects.
     * 
     * @param filter the active filter to determine what filter check corresponds to it.
     */
    public getFilteredArray(objects: TData[], activeFilters: Filter[]) {
        if (activeFilters.length === 0) {
            return objects;
        }

        const allChecks = activeFilters.map(f => this.filterCheckFactory(f))

        return objects.filter(o => this.doesObjectPassChecks(o, allChecks));
    }
}

// ===============================================================================

/**
 * Abstract class for filtering objects. Subclasses must override the factory method
 * which determines which filter checks to instantiate. This abstract class expects a
 * context type which is used to help determine if objects pass a filter. This context
 * is determined by subclasses.
 * 
 * TData is the type of the objects we are filtering
 * TContext is the type of the context the objects may need for filtering
*/
export abstract class FilteringManagerWithContext<TData, TContext> extends FilteringManager<TData> {

    // ===============================================================================

    /**
     * This method uses a given filter id to instantiate the correct filter check class,
     * passing the context to this class if needed.
     * 
     * @param filter the active filter to determine what filter check corresponds to it.
     * @param context filtering context needed by some of the concrete filter checks
     */
    protected abstract filterCheckFactory(filter: Filter, context: TContext): FilterCheck<TData>;

    // ===============================================================================

    /**
     * Given a list of objects, context for those objects, and a list of filters,
     * this method returns the filtered list of those objects.
     * 
     * @param filter the active filter to determine what filter check corresponds to it.
     * @param context filtering context needed by some of the concrete filter checks
     */
    public getFilteredArray(objects: TData[], context: TContext, activeFilters: Filter[]) {
        if (activeFilters.length === 0) {
            return objects;
        }

        const allChecks = activeFilters.map(f => this.filterCheckFactory(f, context)) 

        return objects.filter(o => this.doesObjectPassChecks(o, allChecks));
    }
}